import { defineStore } from 'pinia'

import type { OnboardingProgressData } from '@/services/api/platform.api'

import {
  onboardingProgressCompleteApi,
  onboardingProgressGetApi,
  onboardingProgressSaveApi,
  v7OnboardingTaskAllCompleteApi,
  v7OnboardingTaskCompleteApi,
  v7OnboardingTaskCompleteInfoApi,
} from '@/services/api/platform.api'

const shopifyThemeAppEmbedId = import.meta.env.VITE_SHOPIFY_THEME_APP_EMBED_ID || ''

export type SetupStatus = 'pending' | 'done' | 'skipped' | 'failed'
export type StepType = 'integration' | 'feed' | 'embed'

export interface SetupStep {
  id: string
  toolId: string
  toolName: string
  icon?: string
  title: string
  type: StepType
  route: string
  status: SetupStatus
  taskId?: number // v7 任务 ID
}

export interface SeedPayload {
  industry: string | null
  source: string | null
  painPoints: string[]
  toolsSelected: string[]
}

interface ToolConfig {
  id: string
  name: string
  icon?: string
  capabilities: { integration: boolean, feed: boolean }
  routes: { integration?: string, feed?: string }
  category: 'ads' | 'email' | 'analytics' | 'other'
  taskIds?: { integration?: number, feed?: number } // v7 任务 ID 映射
}

// v7 任务 ID 常量定义
const V7_TASK_IDS = {
  // Integration 任务 (10600-10614)
  FACEBOOK_INTEGRATION: 10600,
  GOOGLE_INTEGRATION: 10601,
  BING_INTEGRATION: 10602,
  TIKTOK_INTEGRATION: 10603,
  LINKEDIN_INTEGRATION: 10604,
  PINTEREST_INTEGRATION: 10605,
  SNAPCHAT_INTEGRATION: 10606,
  KLAVIYO_INTEGRATION: 10607,
  CARTSEE_INTEGRATION: 10608,
  UPPROMOTE_INTEGRATION: 10609,
  IMPACT_INTEGRATION: 10610,
  SHAREASALE_INTEGRATION: 10611,
  APPLOVIN_INTEGRATION: 10612,
  CALLTRACKINGMETRICS_INTEGRATION: 10613,
  OMNISEND_INTEGRATION: 10614,
  // Conversion Feed 任务 (10650-10661)
  FACEBOOK_FEED: 10650,
  GOOGLE_FEED: 10651,
  BING_FEED: 10652,
  TIKTOK_FEED: 10653,
  GA4_FEED: 10654,
  SNAPCHAT_FEED: 10655,
  KLAVIYO_FEED: 10656,
  CARTSEE_FEED: 10657,
  SHAREASALE_FEED: 10658,
  IMPACT_FEED: 10659,
  CYBERTARGETER_FEED: 10660,
  OMNISEND_FEED: 10661,
  // App Embed 任务
  APP_EMBED: 10690,
} as const

const TOOL_CONFIGS: Record<string, ToolConfig> = {
  'meta-ads': { id: 'meta-ads', name: 'Facebook Ads', icon: 'integrations/facebook_filling', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/meta', feed: '/conversion-feed/meta' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.FACEBOOK_INTEGRATION, feed: V7_TASK_IDS.FACEBOOK_FEED } },
  'google-ads': { id: 'google-ads', name: 'Google Ads', icon: 'integrations/google_linear', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/google', feed: '/conversion-feed/google' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.GOOGLE_INTEGRATION, feed: V7_TASK_IDS.GOOGLE_FEED } },
  'tiktok-ads': { id: 'tiktok-ads', name: 'TikTok', icon: 'integrations/tiktok_filling', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/tiktok', feed: '/conversion-feed/tiktok' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.TIKTOK_INTEGRATION, feed: V7_TASK_IDS.TIKTOK_FEED } },
  'pinterest-ads': { id: 'pinterest-ads', name: 'Pinterest', icon: 'integrations/pinterest_filling', capabilities: { integration: true, feed: false }, routes: { integration: '/integrations/pinterest' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.PINTEREST_INTEGRATION } },
  'snapchat-ads': { id: 'snapchat-ads', name: 'Snapchat', icon: 'integrations/snapchat_filling', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/snapchat', feed: '/conversion-feed/snapchat' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.SNAPCHAT_INTEGRATION, feed: V7_TASK_IDS.SNAPCHAT_FEED } },
  'bing-ads': { id: 'bing-ads', name: 'Bing', icon: 'integrations/bing_linear', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/bing', feed: '/conversion-feed/bing' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.BING_INTEGRATION, feed: V7_TASK_IDS.BING_FEED } },
  // 暂时屏蔽
  // 'criteo': { id: 'criteo', name: 'Criteo', icon: 'integrations/criteo_filling', capabilities: { integration: true, feed: false }, routes: { integration: '/integrations/criteo' }, category: 'ads' },
  'impact': { id: 'impact', name: 'Impact', icon: 'integrations/impact_filling', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/impact', feed: '/conversion-feed/impact' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.IMPACT_INTEGRATION, feed: V7_TASK_IDS.IMPACT_FEED } },
  'shareasale': { id: 'shareasale', name: 'ShareASale', icon: 'integrations/shareasale_linear', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/shareasale', feed: '/conversion-feed/shareasale' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.SHAREASALE_INTEGRATION, feed: V7_TASK_IDS.SHAREASALE_FEED } },
  'klaviyo': { id: 'klaviyo', name: 'Klaviyo', icon: 'integrations/klaviyo_filling', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/klaviyo', feed: '/conversion-feed/klaviyo' }, category: 'email', taskIds: { integration: V7_TASK_IDS.KLAVIYO_INTEGRATION, feed: V7_TASK_IDS.KLAVIYO_FEED } },
  'omnisend': { id: 'omnisend', name: 'Omnisend', icon: 'integrations/omnisend_filling', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/omnisend', feed: '/conversion-feed/omnisend' }, category: 'email', taskIds: { integration: V7_TASK_IDS.OMNISEND_INTEGRATION, feed: V7_TASK_IDS.OMNISEND_FEED } },
  // 暂时屏蔽
  // 'mailchimp': { id: 'mailchimp', name: 'Mailchimp', icon: 'integrations/mailchimp_linear', capabilities: { integration: true, feed: true }, routes: { integration: '/integrations/mailchimp', feed: '/conversion-feed/mailchimp' }, category: 'email' },
  'ga4': { id: 'ga4', name: 'GA4', icon: 'integrations/ga4', capabilities: { integration: false, feed: true }, routes: { feed: '/conversion-feed/ga4' }, category: 'analytics', taskIds: { feed: V7_TASK_IDS.GA4_FEED } },
  'linkedin': { id: 'linkedin', name: 'LinkedIn', icon: 'integrations/linkedin_filling', capabilities: { integration: true, feed: false }, routes: { integration: '/integrations/linkedin' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.LINKEDIN_INTEGRATION } },
  'applovin': { id: 'applovin', name: 'AppLovin', icon: 'integrations/applovin', capabilities: { integration: true, feed: false }, routes: { integration: '/integrations/applovin' }, category: 'ads', taskIds: { integration: V7_TASK_IDS.APPLOVIN_INTEGRATION } },
  'calltrackingmetrics': { id: 'calltrackingmetrics', name: 'CallTrackingMetrics', icon: 'integrations/calltrackingmetrics', capabilities: { integration: true, feed: false }, routes: { integration: '/integrations/calltrackingmetrics' }, category: 'other', taskIds: { integration: V7_TASK_IDS.CALLTRACKINGMETRICS_INTEGRATION } },
}

const PRIORITY: string[] = [
  'meta-ads',
  'google-ads',
  'klaviyo',
  'tiktok-ads',
  'pinterest-ads',
  'snapchat-ads',
  'bing-ads',
  // 'mailchimp', // 暂时屏蔽
  'omnisend',
  'impact',
  'shareasale',
  // 'criteo', // 暂时屏蔽
  'ga4',
]

export const useOnboardingSetupStore = defineStore('onboarding-setup', {
  state: () => ({
    industry: null as string | null,
    source: null as string | null,
    painPoints: [] as string[],
    toolsSelected: [] as string[],
    steps: [] as SetupStep[],
    currentIndex: 0,
    modalOpen: false,
    minimized: false,
    completedAt: null as string | null,
    // 新增：步骤完成后居中弹窗显示
    congratsOpen: false,
    // 新增：所有任务是否完成的标记（来自后端）
    allTasksFinished: false,
    // 新增：用于等待弹窗关闭的 resolve 函数
    _congratsResolver: null as ((value: void) => void) | null,
    // 新增：历史已完成的任务 ID 列表（用于判断是否需要显示 congrats）
    historicallyCompletedTaskIds: new Set<number>(),
    // 新增：onboarding版本控制
    onboardingVersion: 'v1' as string,
    // 新增：是否使用新版onboarding（false表示使用老版本v7 task）
    useNewOnboarding: false as boolean,
    // 新增：问卷状态（用于恢复问卷页面的进度）
    questionnaireState: null as {
      currentQuestion?: string
      sourceDetail?: string | null
      painOther?: string | null
    } | null,
  }),
  getters: {
    total: s => s.steps.length,
    currentStep: s => s.steps[s.currentIndex] || null,
    // 所有任务是否实际完成（基于本地步骤状态）
    allTasksCompleted: s => s.steps.length > 0 && s.steps.every(st => st.status === 'done' || st.status === 'skipped'),
    // 用户是否已标记完成（基于后端标记，用户点击了 Finish 按钮）
    isFinished: s => s.allTasksFinished,
    hasQueue: s => s.steps.length > 0,
    nextStep: (s) => {
      // Return the first unfinished step anywhere in the list (pending or failed)
      const i = s.steps.findIndex(st => st.status !== 'done' && st.status !== 'skipped')
      return i >= 0 ? s.steps[i] : null
    },
  },
  actions: {
    // helper: build final embed step
    _buildFinalEmbedStep(): SetupStep {
      return {
        id: 'app-embed',
        toolId: 'app-embed',
        toolName: 'Track every customer event',
        icon: 'simple-icons:shopify',
        title: 'Enable App Embed',
        type: 'embed',
        route: `/admin/themes/current/editor?context=apps&activateAppId=${shopifyThemeAppEmbedId}/app-embed`,
        status: 'pending',
        taskId: V7_TASK_IDS.APP_EMBED,
      }
    },
    ensureFinalEmbedStep() {
      if (!this.steps.some(s => s.id === 'app-embed')) {
        this.steps.push(this._buildFinalEmbedStep())
      }
    },
    async seedFromOnboarding(payload: SeedPayload, saveToServer = true) {
      this.industry = payload.industry
      this.source = payload.source
      this.painPoints = payload.painPoints
      this.toolsSelected = payload.toolsSelected
      this.useNewOnboarding = true // 新用户使用新版
      this.generateQueue()

      // 保存到后端（可选）
      if (saveToServer) {
        try {
          await this.saveToBackend()
        }
        catch (error) {
          console.error('Failed to save onboarding progress:', error)
        }
      }
    },

    // 保存问卷部分进度（用于问卷页面切换问题时保存）
    async saveQuestionnaireProgress(data: {
      industry?: string | null
      source?: string | null
      sourceDetail?: string | null
      painPoints?: string[]
      painOther?: string | null
      toolsSelected?: string[]
      currentQuestion?: string
    }) {
      // 更新 store 状态
      if (data.industry !== undefined)
        this.industry = data.industry
      if (data.source !== undefined)
        this.source = data.source
      if (data.painPoints !== undefined)
        this.painPoints = data.painPoints
      if (data.toolsSelected !== undefined)
        this.toolsSelected = data.toolsSelected
      this.useNewOnboarding = true

      // 保存到后端
      try {
        const progressData: Record<string, any> = {
          questionnaire: {
            industry: this.industry,
            source: this.source,
            sourceDetail: data.sourceDetail || null,
            painPoints: this.painPoints,
            painOther: data.painOther || null,
            toolsSelected: this.toolsSelected,
            currentQuestion: data.currentQuestion || 'source',
          },
          steps: this.steps,
          stepStatus: this.steps.reduce((acc, step) => {
            acc[step.id] = step.status
            return acc
          }, {} as Record<string, SetupStatus>),
          completedTaskIds: Array.from(this.historicallyCompletedTaskIds),
          is_completed: false,
        }

        await onboardingProgressSaveApi({
          version: this.onboardingVersion,
          current_step: this.currentIndex,
          current_platform: this.currentStep?.toolId || '',
          progress_data: progressData,
          is_completed: false,
        })
      }
      catch (error) {
        console.error('Failed to save questionnaire progress:', error)
      }
    },
    generateQueue() {
      // 过滤为项目支持的工具 ID
      const selected = [...this.toolsSelected].filter(t => !!TOOL_CONFIGS[t])

      let chosen: string[] = []
      // 规则 4：仅选了 other（或仅选了不受支持的自定义）时，回退为 meta、google、klaviyo
      if (selected.length === 0) {
        chosen = ['meta-ads', 'google-ads', 'klaviyo'].filter(t => TOOL_CONFIGS[t])
      }
      else {
        // 规则 1：优先展示 meta、google
        if (selected.includes('meta-ads'))
          chosen.push('meta-ads')
        if (selected.includes('google-ads'))
          chosen.push('google-ads')

        // 规则 2：邮件工具优先 klaviyo，否则择 omnisend
        if (selected.includes('klaviyo'))
          chosen.push('klaviyo')
        else if (selected.includes('omnisend'))
          chosen.push('omnisend')
        // mailchimp 暂时屏蔽
        // else if (selected.includes('mailchimp'))
        //   chosen.push('mailchimp')

        // 填充剩余名额：根据既有 PRIORITY 顺序，从用户选择中补足至最多 3 个
        const remaining = selected
          .filter(t => !chosen.includes(t))
          .sort((a, b) => PRIORITY.indexOf(a) - PRIORITY.indexOf(b))
        for (const t of remaining) {
          if (chosen.length >= 3)
            break
          chosen.push(t)
        }
      }

      // 限制最多展示 3 个工具，并去重
      chosen = Array.from(new Set(chosen)).slice(0, 3)

      const steps: SetupStep[] = []
      for (const toolId of chosen) {
        const cfg = TOOL_CONFIGS[toolId]
        if (!cfg)
          continue
        // integration first
        if (cfg.capabilities.integration && cfg.routes.integration) {
          steps.push({
            id: `${toolId}-integration`,
            toolId,
            toolName: cfg.name,
            icon: cfg.icon,
            title: `Connect ${cfg.name}`,
            type: 'integration',
            route: cfg.routes.integration!,
            status: 'pending',
            taskId: cfg.taskIds?.integration,
          })
        }
        // then feed
        if (cfg.capabilities.feed && cfg.routes.feed) {
          steps.push({
            id: `${toolId}-feed`,
            toolId,
            toolName: cfg.name,
            icon: cfg.icon,
            title: `Set up conversion feed for ${cfg.name}`,
            type: 'feed',
            route: cfg.routes.feed!,
            status: 'pending',
            taskId: cfg.taskIds?.feed,
          })
        }
      }
      // 保留最终的 App Embed 步骤
      steps.push(this._buildFinalEmbedStep())
      this.steps = steps
      this.currentIndex = 0
    },
    openModal() {
      this.modalOpen = true
      this.ensureFinalEmbedStep()
    },
    closeModal() { this.modalOpen = false },
    startFlow() {
      this.modalOpen = false
      this.minimized = true
      this.ensureFinalEmbedStep()
    },
    backToSteps() {
      this.modalOpen = true
      this.minimized = false
      this.ensureFinalEmbedStep()
    },
    // 获取恢复步骤的路径（用于新版 progress 模式）
    // 根据当前进度计算用户应该继续的步骤页面
    getResumeStepPath(): string | null {
      // pain-complete 状态：特殊流程（De-anonymize/Influencer/Other）跳过了 tools 选择
      // 这些流程有各自的入口页面，需要先经过入口页面再进入 google-steps
      if (this.questionnaireState?.currentQuestion === 'pain-complete' && this.painPoints.length > 0) {
        const pain = this.painPoints[0]
        if (pain === 'De-anonymize visitors and grow email list') {
          return '/onboarding/de-anonymize-steps/step1'
        }
        if (pain === 'Track and attribute influencer performance') {
          return '/onboarding/influencer-steps/step1'
        }
        if (pain === 'I have other goals') {
          return '/onboarding/google-steps/step1'
        }
      }

      // 如果没有步骤，返回问卷页面
      if (this.steps.length === 0) {
        return '/onboarding'
      }

      // 查找第一个未完成的步骤
      const pendingStep = this.steps.find(s => s.status !== 'done' && s.status !== 'skipped')

      if (!pendingStep) {
        // 所有步骤都完成了，返回 app-embed 页面
        return '/onboarding/goal-steps/step3'
      }

      // 根据步骤类型和工具返回对应的页面
      const toolId = pendingStep.toolId
      const stepType = pendingStep.type

      // 工具 ID 到 URL slug 的映射
      const toolSlugMap: Record<string, string> = {
        'meta-ads': 'meta',
        'google-ads': 'google',
        'tiktok-ads': 'tiktok',
        'pinterest-ads': 'pinterest',
        'snapchat-ads': 'snapchat',
        'bing-ads': 'bing',
        'klaviyo': 'klaviyo',
        'omnisend': 'omnisend',
        'impact': 'impact',
        'shareasale': 'shareasale',
        'linkedin': 'linkedin',
        'applovin': 'applovin',
        'calltrackingmetrics': 'calltrackingmetrics',
        'ga4': 'ga4',
      }

      const slug = toolSlugMap[toolId]

      if (stepType === 'embed') {
        // App embed 步骤
        return '/onboarding/goal-steps/step3'
      }

      if (!slug) {
        // 未知工具，返回问卷页面
        return '/onboarding'
      }

      if (stepType === 'integration') {
        return `/onboarding/${slug}-steps/step1`
      }

      if (stepType === 'feed') {
        return `/onboarding/${slug}-steps/step2`
      }

      return '/onboarding'
    },
    // 新增：Congrats 弹窗控制
    openCongrats() { this.congratsOpen = true },
    closeCongrats() {
      this.congratsOpen = false
      // 如果有等待的 resolver，调用它
      if (this._congratsResolver) {
        this._congratsResolver()
        this._congratsResolver = null
      }
    },
    async markDone(stepId?: string) {
      const st = stepId ? this.steps.find(s => s.id === stepId) : this.steps[this.currentIndex]
      if (!st)
        return

      // 检查该任务是否之前已完成过
      const wasHistoricallyCompleted = st.taskId !== undefined && this.historicallyCompletedTaskIds.has(st.taskId)

      // 标记为完成
      st.status = 'done'

      // 根据版本调用不同的后台接口
      try {
        if (this.useNewOnboarding) {
          // 新版：保存整个 progress
          await this.saveToBackend()
        }
        else if (st.taskId !== undefined) {
          // 老版：调用 v7 task 完成接口
          await v7OnboardingTaskCompleteApi({ task_id: st.taskId })
        }
      }
      catch (error) {
        console.error('Failed to mark task as complete:', error)
        // 即使后台调用失败，也保持本地状态为 done（用户体验优先）
      }

      // 只有在该任务之前未完成过时，才打开居中祝贺弹窗
      if (!wasHistoricallyCompleted) {
        this.openCongrats()

        // 等待弹窗关闭后再继续
        await new Promise<void>((resolve) => {
          this._congratsResolver = resolve
        })
      }

      // 将当前任务标记为历史已完成
      if (st.taskId !== undefined) {
        this.historicallyCompletedTaskIds.add(st.taskId)
      }
    },
    markFailed(stepId?: string) {
      const st = stepId ? this.steps.find(s => s.id === stepId) : this.steps[this.currentIndex]
      if (!st)
        return
      st.status = 'failed'
    },
    async skip(stepId?: string) {
      const st = stepId ? this.steps.find(s => s.id === stepId) : this.steps[this.currentIndex]
      if (!st)
        return
      st.status = 'skipped'

      // 新版本下保存状态到后端
      if (this.useNewOnboarding) {
        try {
          await this.saveToBackend()
        }
        catch (error) {
          console.error('Failed to save skipped status:', error)
        }
      }
    },
    goNext() {
      // Jump to the first unfinished step regardless of position
      const i = this.steps.findIndex(st => st.status !== 'done' && st.status !== 'skipped')
      if (i >= 0) {
        this.currentIndex = i

        // 新版本下保存当前索引到后端
        if (this.useNewOnboarding) {
          this.saveToBackend().catch((error) => {
            console.error('Failed to save current index:', error)
          })
        }
      }
    },
    // 前进到下一步并保存进度（用于新版 onboarding 步骤页面）
    // stepId: 当前完成的步骤 ID（可选，用于标记为 done）
    // markAsDone: 是否标记当前步骤为已完成
    async advanceToNextStep(stepId?: string, markAsDone = false) {
      // 如果提供了 stepId 且需要标记为完成
      if (stepId && markAsDone) {
        const st = this.steps.find(s => s.id === stepId)
        if (st) {
          st.status = 'done'
        }
      }

      // 更新 currentIndex 到下一个未完成的步骤
      const nextIndex = this.steps.findIndex(st => st.status !== 'done' && st.status !== 'skipped')
      if (nextIndex >= 0) {
        this.currentIndex = nextIndex
      }

      // 新版本下保存状态到后端
      if (this.useNewOnboarding) {
        try {
          await this.saveToBackend()
        }
        catch (error) {
          console.error('Failed to save progress:', error)
        }
      }
    },

    // 跳过当前步骤并前进到下一步（用于 Skip 按钮）
    // stepId: 要跳过的步骤 ID（可选，默认使用当前步骤）
    async skipAndAdvance(stepId?: string) {
      // 标记步骤为 skipped
      const st = stepId ? this.steps.find(s => s.id === stepId) : this.steps[this.currentIndex]
      if (st) {
        st.status = 'skipped'
      }

      // 更新 currentIndex 到下一个未完成的步骤
      const nextIndex = this.steps.findIndex(s => s.status !== 'done' && s.status !== 'skipped')
      if (nextIndex >= 0) {
        this.currentIndex = nextIndex
      }

      // 新版本下保存状态到后端
      if (this.useNewOnboarding) {
        try {
          await this.saveToBackend()
        }
        catch (error) {
          console.error('Failed to save skipped progress:', error)
        }
      }
    },
    // 根据当前路径更新 currentIndex 并保存（用于页面加载时同步状态）
    async syncCurrentStepByPath(path: string) {
      // 从路径中解析出当前的工具和步骤类型
      const match = path.match(/\/onboarding\/([^/]+)-steps\/step(\d+)/)
      if (!match)
        return

      const [, toolSlug, stepNum] = match
      const toolIdMap: Record<string, string> = {
        meta: 'meta-ads',
        google: 'google-ads',
        tiktok: 'tiktok-ads',
        pinterest: 'pinterest-ads',
        snapchat: 'snapchat-ads',
        bing: 'bing-ads',
        klaviyo: 'klaviyo',
        omnisend: 'omnisend',
        impact: 'impact',
        shareasale: 'shareasale',
        linkedin: 'linkedin',
        applovin: 'applovin',
        calltrackingmetrics: 'calltrackingmetrics',
        ga4: 'ga4',
      }

      const toolId = toolIdMap[toolSlug]
      if (!toolId)
        return

      // 根据 step 号确定类型：step1 = integration, step2 = feed
      const stepType = stepNum === '1' ? 'integration' : 'feed'
      const targetStepId = `${toolId}-${stepType}`

      // 找到对应的步骤索引
      const stepIndex = this.steps.findIndex(s => s.id === targetStepId)
      if (stepIndex >= 0) {
        this.currentIndex = stepIndex

        // 新版本下保存状态
        if (this.useNewOnboarding) {
          try {
            await this.saveToBackend()
          }
          catch (error) {
            console.error('Failed to save current step:', error)
          }
        }
      }
    },
    reset() {
      this.steps = []
      this.currentIndex = 0
      this.modalOpen = false
      this.minimized = false
      this.industry = null
      this.source = null
      this.painPoints = []
      this.toolsSelected = []
      this.allTasksFinished = false
      this.historicallyCompletedTaskIds.clear()
      this.useNewOnboarding = false
    },
    // 完成全部任务
    async finishAll() {
      try {
        if (this.useNewOnboarding) {
          // 新版：调用 complete 接口
          await onboardingProgressCompleteApi({ version: this.onboardingVersion })
          if (!this.completedAt)
            this.completedAt = new Date().toISOString()
        }
        else {
          // 老版：调用 v7 all complete 接口
          await v7OnboardingTaskAllCompleteApi()
        }
        this.allTasksFinished = true
      }
      catch (error) {
        console.error('Failed to mark all tasks as complete:', error)
        throw error
      }
    },
    // 获取所有步骤的 task IDs 用于初始化
    getAllTaskIds(): number[] {
      return this.steps
        .map(step => step.taskId)
        .filter((id): id is number => id !== undefined)
    },
    // 根据后台返回的任务完成状态更新本地步骤状态
    syncTaskStatus(taskStatusList: Array<{ task_id: number, is_finished: boolean }>) {
      const statusMap = new Map(taskStatusList.map(item => [item.task_id, item.is_finished]))

      // 清空并重新构建历史已完成任务列表
      this.historicallyCompletedTaskIds.clear()

      this.steps.forEach((step) => {
        if (step.taskId !== undefined && statusMap.has(step.taskId)) {
          const isFinished = statusMap.get(step.taskId)
          if (isFinished) {
            step.status = 'done'
            // 记录历史已完成的任务
            this.historicallyCompletedTaskIds.add(step.taskId)
          }
          else if (step.status === 'done') {
            // 如果本地标记为完成但后台未完成，保持本地状态（可能是待同步）
            // 或者根据业务需求重置为 pending
          }
        }
      })
    },
    // 根据 taskId 查找步骤
    findStepByTaskId(taskId: number): SetupStep | undefined {
      return this.steps.find(step => step.taskId === taskId)
    },
    // 从后端加载并恢复状态
    // 返回值：
    //   'new' - 需要走新版onboarding流程（显示问卷）
    //   'questionnaire' - 问卷正在进行中（需要恢复到问卷页面的某个问题）
    //   'legacy' - 使用老版本v7 task（已有任务，显示floating guide）
    //   'progress' - 使用新版progress（已有进度，显示floating guide）
    //   'completed' - 已完成所有onboarding（不显示任何引导）
    // 抛出错误：接口请求失败时抛出错误，由调用方处理
    async loadFromBackend(): Promise<'new' | 'questionnaire' | 'legacy' | 'progress' | 'completed'> {
      try {
        // 1. 先尝试获取新版 onboarding progress
        const progressData = await onboardingProgressGetApi({ version: this.onboardingVersion })

        if (progressData) {
          // 新版有数据，使用新版逻辑
          this.useNewOnboarding = true
          this.allTasksFinished = progressData.is_completed
          this.completedAt = progressData.completed_at || null

          // 即使已完成，也需要恢复问卷信息（Goal/Tools）用于后续价值验证展示
          this.restoreFromProgressData(progressData)

          // 如果已完成，直接返回
          if (progressData.is_completed) {
            return 'completed'
          }

          // 恢复后检查：如果没有步骤数据但有问卷数据，说明用户在问卷中途
          if (this.steps.length === 0 && this.toolsSelected.length === 0) {
            // 检查是否有问卷部分进度
            if (this.questionnaireState && this.questionnaireState.currentQuestion) {
              // 'pain-complete' 表示用户已完成 pain 步骤并跳过了 tools（De-anonymize/Influencer/Other 流程）
              // 此时应该直接恢复到对应的步骤页面，而非返回问卷
              if (this.questionnaireState.currentQuestion === 'pain-complete') {
                return 'progress'
              }
              return 'questionnaire'
            }
            return 'new'
          }

          // 新版不显示 floating guide，用户会被重定向到相应的步骤页面继续
          // this.minimized = true  // 不再设置 minimized

          return 'progress'
        }

        // 2. 新版没有数据，尝试老版本 v7 task 接口
        const taskStatusResponse = await v7OnboardingTaskCompleteInfoApi()

        if (taskStatusResponse && taskStatusResponse.task_info && taskStatusResponse.task_info.length > 0) {
          // 老版本有任务，使用老版本逻辑
          this.useNewOnboarding = false
          this.allTasksFinished = taskStatusResponse.all_task_info

          // 如果已完成，直接返回
          if (taskStatusResponse.all_task_info) {
            return 'completed'
          }

          // 根据任务状态重建步骤列表
          this.rebuildStepsFromTaskStatus(taskStatusResponse.task_info)

          // 同步任务完成状态
          this.syncTaskStatus(taskStatusResponse.task_info)

          // 显示 floating guide
          if (!this.allTasksFinished) {
            this.minimized = true
          }

          return 'legacy'
        }

        // 3. 新版和老版都没有数据，需要走新版 onboarding 流程
        this.useNewOnboarding = true
        return 'new'
      }
      catch (error) {
        console.error('Failed to load onboarding state from backend:', error)
        throw error
      }
    },

    // 从 progress_data 恢复状态
    restoreFromProgressData(data: OnboardingProgressData) {
      const progressData = data.progress_data || {}

      // 恢复问卷数据
      if (progressData.questionnaire) {
        const q = progressData.questionnaire as Record<string, any>
        this.industry = q.industry || null
        this.source = q.source || null
        this.painPoints = q.painPoints || []
        this.toolsSelected = q.toolsSelected || []

        // 恢复问卷详细状态（用于恢复问卷页面的进度）
        this.questionnaireState = {
          currentQuestion: q.currentQuestion || 'source',
          sourceDetail: q.sourceDetail || null,
          painOther: q.painOther || null,
        }
      }

      // 恢复步骤列表
      if (progressData.steps && Array.isArray(progressData.steps)) {
        this.steps = progressData.steps as SetupStep[]
      }
      else if (this.toolsSelected.length > 0) {
        // 如果没有保存 steps 但有 toolsSelected，重新生成
        this.generateQueue()
      }

      // 恢复步骤状态
      if (progressData.stepStatus && typeof progressData.stepStatus === 'object') {
        const stepStatus = progressData.stepStatus as Record<string, SetupStatus>
        this.steps.forEach((step) => {
          if (stepStatus[step.id]) {
            step.status = stepStatus[step.id]
          }
        })
      }

      // 恢复历史完成的任务
      if (progressData.completedTaskIds && Array.isArray(progressData.completedTaskIds)) {
        this.historicallyCompletedTaskIds = new Set(progressData.completedTaskIds as number[])
      }

      // 恢复当前步骤索引
      this.currentIndex = data.current_step || 0
    },

    // 保存状态到后端（新版）
    async saveToBackend(isComplete = false) {
      if (!this.useNewOnboarding) {
        // 老版本不需要保存 progress，任务完成通过其他接口
        return
      }

      const progressData: Record<string, any> = {
        questionnaire: {
          industry: this.industry,
          source: this.source,
          painPoints: this.painPoints,
          toolsSelected: this.toolsSelected,
          // 保留问卷详细状态（currentQuestion, sourceDetail, painOther）
          ...(this.questionnaireState
            ? {
                currentQuestion: this.questionnaireState.currentQuestion,
                sourceDetail: this.questionnaireState.sourceDetail || null,
                painOther: this.questionnaireState.painOther || null,
              }
            : {}),
        },
        steps: this.steps,
        stepStatus: this.steps.reduce((acc, step) => {
          acc[step.id] = step.status
          return acc
        }, {} as Record<string, SetupStatus>),
        completedTaskIds: Array.from(this.historicallyCompletedTaskIds),
        is_completed: isComplete,
      }

      await onboardingProgressSaveApi({
        version: this.onboardingVersion,
        current_step: this.currentIndex,
        current_platform: this.currentStep?.toolId || '',
        progress_data: progressData,
        is_completed: isComplete,
      })

      if (isComplete) {
        this.allTasksFinished = true
        if (!this.completedAt)
          this.completedAt = new Date().toISOString()
      }
    },
    // 根据任务状态重建步骤列表
    rebuildStepsFromTaskStatus(taskStatus: Array<{ task_id: number, is_finished: boolean }>) {
      const taskIds = taskStatus.map(t => t.task_id)
      const steps: SetupStep[] = []

      // 遍历所有工具配置，找到匹配的任务
      for (const [toolId, cfg] of Object.entries(TOOL_CONFIGS)) {
        // Integration
        if (cfg.taskIds?.integration && taskIds.includes(cfg.taskIds.integration)) {
          steps.push({
            id: `${toolId}-integration`,
            toolId,
            toolName: cfg.name,
            icon: cfg.icon,
            title: `Connect ${cfg.name}`,
            type: 'integration',
            route: cfg.routes.integration!,
            status: 'pending',
            taskId: cfg.taskIds.integration,
          })
        }

        // Feed
        if (cfg.taskIds?.feed && taskIds.includes(cfg.taskIds.feed)) {
          steps.push({
            id: `${toolId}-feed`,
            toolId,
            toolName: cfg.name,
            icon: cfg.icon,
            title: `Set up conversion feed for ${cfg.name}`,
            type: 'feed',
            route: cfg.routes.feed!,
            status: 'pending',
            taskId: cfg.taskIds.feed,
          })
        }
      }

      // App Embed
      if (taskIds.includes(V7_TASK_IDS.APP_EMBED)) {
        steps.push(this._buildFinalEmbedStep())
      }

      // 按工具优先级排序
      steps.sort((a, b) => {
        const aIndex = PRIORITY.indexOf(a.toolId)
        const bIndex = PRIORITY.indexOf(b.toolId)
        if (aIndex === -1 && bIndex === -1)
          return 0
        if (aIndex === -1)
          return 1
        if (bIndex === -1)
          return -1
        return aIndex - bIndex
      })

      this.steps = steps
      this.currentIndex = 0
    },
  },
})
